import type { HTMLAttributes } from 'react';
import React, { forwardRef, useEffect, useRef } from 'react';
import { useEffectEvent } from 'use-effect-event';
import Slot from '../components/Slot';

const TOUCH_EVENT = { react: 'onTouchStart', native: 'touchstart' } as const;
const MOUSE_EVENT = { react: 'onMouseDown', native: 'mousedown' } as const;

const setUpReactEventHandlers = (
  handler: () => void,
  props: HTMLAttributes<unknown>,
): HTMLAttributes<unknown> => ({
  ...props,
  [TOUCH_EVENT.react]: (e) => {
    handler();
    props[TOUCH_EVENT.react]?.(e);
  },
  [MOUSE_EVENT.react]: (e) => {
    handler();
    props[MOUSE_EVENT.react]?.(e);
  },
});

interface ClickOutsideHandlerProps extends HTMLAttributes<HTMLDivElement> {
  onClickOutside?: ((e: Event) => unknown) | null;
  asChild?: boolean;
}

const ClickOutsideHandler = forwardRef<
  HTMLDivElement,
  ClickOutsideHandlerProps
>(({ onClickOutside, asChild, ...props }: ClickOutsideHandlerProps, ref) => {
  const isDisabled = !onClickOutside;

  // To support React Portal.
  // Alternative to https://github.com/facebook/react/issues/14540
  const isTargetInsideReactTreeRef = useRef(false);

  const onClickOutsideEvent = useEffectEvent(onClickOutside ?? (() => {}));

  useEffect(() => {
    if (isDisabled) return undefined;

    const handler = (e: Event) => {
      if (!isTargetInsideReactTreeRef.current) {
        onClickOutsideEvent(e);
      }

      isTargetInsideReactTreeRef.current = false;
    };

    document.addEventListener(TOUCH_EVENT.native, handler, { passive: true });
    document.addEventListener(MOUSE_EVENT.native, handler, { passive: true });
    return () => {
      document.removeEventListener(TOUCH_EVENT.native, handler);
      document.removeEventListener(MOUSE_EVENT.native, handler);
    };
  }, [isDisabled]);

  const handleReactEvent = () => {
    isTargetInsideReactTreeRef.current = true;
  };

  const Comp = asChild ? Slot : 'div';

  return (
    <Comp
      ref={ref}
      {...(isDisabled
        ? props
        : setUpReactEventHandlers(handleReactEvent, props))}
    />
  );
});

export default ClickOutsideHandler;
